/**
 * Entity Cache Service
 * 
 * Provides persistent IndexedDB caching for entity metadata.
 * 
 * ARCHITECTURE:
 * - Stores metadata-only (no document content) in browser IndexedDB
 * - Persists across sessions (survives page refresh, browser close)
 * - Indexed by user ID for multi-tenant support
 * - Version-aware for cache invalidation
 * 
 * BENEFITS:
 * - First load: 0.5-2s (from backend with indexes)
 * - Subsequent loads: 50-100ms (from IndexedDB)
 * - Works offline for entity browsing
 * 
 * SAFETY:
 * - Only caches metadata (name, id, llmId, etc.)
 * - Document content ALWAYS fetched fresh via entityService.get()
 * - Cache invalidation on version mismatch
 */

import { BaseEntityType } from '../types';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('ENTITY_CACHE');

const DB_NAME = 'river_entity_cache';
const DB_VERSION = 1;
const STORE_NAME = 'entities';
const CACHE_VERSION_KEY = 'cache_version';
const CURRENT_CACHE_VERSION = '1.0.1';  // ✅ BUMPED: Invalidate cache to fix missing spaceId bug

interface CacheEntry {
  userId: string;
  entities: BaseEntityType[];
  timestamp: number;  // For logging/debug only, NOT for staleness detection
  version: string;
  entitiesHash: string;  // Hash of all entity IDs + updated_at timestamps
  entityTimestamps: Record<string, string>; // Map of llmId -> updated_at
}

class EntityCacheService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.db) return;
    
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        log.error('[EntityCache] Failed to open IndexedDB:', request.error);
        // ✅ FIX: Clear promise on error to allow retry
        this.initPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        log.info('[EntityCache] IndexedDB initialized successfully');
        // ✅ FIX: Clear promise after success to free memory
        this.initPromise = null;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // ✅ OPTIMIZATION: No indexes needed - userId is the keyPath (already O(1) lookup)
          db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
          log.info('[EntityCache] Created object store');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Compute hash from entity timestamps for staleness detection
   */
  computeEntitiesHash(timestamps: Record<string, string>): string {
    // Sort entries by llmId for deterministic hash
    const sortedEntries = Object.entries(timestamps)
      .sort(([a], [b]) => a.localeCompare(b));
    
    // Create string representation
    const hashInput = sortedEntries
      .map(([llmId, updatedAt]) => `${llmId}:${updatedAt}`)
      .join('|');
    
    // Compute simple hash (fast and sufficient for staleness detection)
    return this.simpleStringHash(hashInput);
  }

  /**
   * Simple string hash function (32-bit FNV-1a)
   */
  private simpleStringHash(str: string): string {
    let hash = 2166136261; // FNV offset basis
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    // Convert to unsigned 32-bit integer and then to base-36 string
    return (hash >>> 0).toString(36);
  }

  /**
   * Flatten hierarchical entity tree to flat array.
   * Necessary for hash computation to include ALL entities, not just top-level.
   * 
   * CRITICAL: Without this, nested entities (docs in folders) are excluded from hash,
   * breaking staleness detection for cross-device sync.
   */
  private flattenEntities(entities: BaseEntityType[]): BaseEntityType[] {
    const result: BaseEntityType[] = [];
    
    const traverse = (entity: BaseEntityType) => {
      // Add current entity to result
      result.push(entity);
      
      // Recursively traverse children if they exist
      if (entity.children && Array.isArray(entity.children)) {
        entity.children.forEach(traverse);
      }
    };
    
    entities.forEach(traverse);
    return result;
  }

  /**
   * Get cached entities for a user
   * 
   * Returns null if:
   * - No cache exists
   * - Cache version mismatch
   * 
   * Note: No time-based staleness checks - hash-only detection!
   */
  async get(userId: string): Promise<CacheEntry | null> {
    try {
      await this.init();
      
      if (!this.db) {
        log.warn('[EntityCache] Database not initialized');
        return null;
      }

      return new Promise(async (resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.get(userId);

        request.onsuccess = async () => {
          const entry = request.result as CacheEntry | undefined;
          
          if (!entry) {
            log.info('[EntityCache] No cache found for user');
            resolve(null);
            return;
          }

          // Check version
          if (entry.version !== CURRENT_CACHE_VERSION) {
            log.info('[EntityCache] Cache version mismatch, invalidating', {
              cached: entry.version,
              current: CURRENT_CACHE_VERSION
            });
            // ✅ CRITICAL FIX: Await delete to ensure it completes
            await this.delete(userId);
            resolve(null);
            return;
          }

          // ✅ NEW: No time-based staleness check - hash-only detection!
          // timestamp is kept for logging/debug purposes only
          const age = Date.now() - entry.timestamp;
          
          log.info('[EntityCache] ✅ Cache hit!', {
            entityCount: entry.entities.length,
            ageMinutes: (age / (60 * 1000)).toFixed(1),
            hash: entry.entitiesHash?.substring(0, 8) || 'none'
          });
          
          // Return full cache entry (including hash and timestamps)
          resolve(entry);
        };

        request.onerror = () => {
          log.error('[EntityCache] Failed to read cache:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Store entities in cache for a user
   * 
   * CRITICAL: Only stores metadata - document content is stripped by EntityStore
   */
  async set(userId: string, entities: BaseEntityType[]): Promise<void> {
    try {
      await this.init();
      
      if (!this.db) {
        log.warn('[EntityCache] Database not initialized, skipping cache write');
        return;
      }

      // Verify entities don't contain document content (safety check)
      const hasDocContent = entities.some(e => 
        e.entityType === 'Doc' && 
        (e.skeleton as any)?.base64 && 
        (e.skeleton as any).base64.length > 100
      );
      
      if (hasDocContent) {
        log.error('[EntityCache] ⚠️ SAFETY VIOLATION: Attempted to cache document content!');
        log.error('[EntityCache] This should never happen - EntityStore should strip base64');
        return;
      }

      // ✅ CRITICAL FIX: Flatten hierarchical entities before extracting timestamps
      // Without this, nested entities (docs in folders) are excluded from hash,
      // breaking staleness detection for cross-device sync
      const flatEntities = this.flattenEntities(entities);
      
      log.info('[EntityCache] 📊 Flattened entities for hash computation', {
        hierarchicalCount: entities.length,
        flatCount: flatEntities.length,
        topLevelEntities: entities.map(e => ({ 
          llmId: e.llmId, 
          name: e.name, 
          hasChildren: !!(e.children && e.children.length > 0)
        }))
      });

      // ✅ Extract timestamps from ALL entities (flat array)
      const entityTimestamps: Record<string, string> = {};
      const entitiesInHash: Array<{llmId: string; name: string; updated_at: string}> = [];
      
      for (const entity of flatEntities) {
        if (entity.llmId && entity.updated_at) {
          entityTimestamps[entity.llmId] = entity.updated_at;
          entitiesInHash.push({
            llmId: entity.llmId,
            name: entity.name,
            updated_at: entity.updated_at
          });
        }
      }
      
      // ✅ Compute hash from complete timestamp map
      const entitiesHash = this.computeEntitiesHash(entityTimestamps);

      log.info('[EntityCache] 🔑 Computed hash from entities', {
        hash: entitiesHash,  // ✅ Full hash for comparison
        hashPreview: entitiesHash.substring(0, 8),
        timestampCount: Object.keys(entityTimestamps).length,
        entitiesWithoutLlmId: flatEntities.length - Object.keys(entityTimestamps).length,
        sampleEntities: entitiesInHash.slice(0, 5).map(e => `${e.name} (${e.llmId})`),
        allEntitiesInHash: entitiesInHash.length <= 20 ? entitiesInHash.map(e => e.name) : '(too many to list)'
      });

      const entry: CacheEntry = {
        userId,
        entities,
        timestamp: Date.now(),  // For logging/debug only
        version: CURRENT_CACHE_VERSION,
        entitiesHash,           // ✅ NEW: For staleness detection
        entityTimestamps,       // ✅ NEW: For identifying stale entities
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.put(entry);

        request.onsuccess = () => {
          log.info('[EntityCache] ✅ Cache updated successfully', {
            entityCount: entities.length,
            hash: entitiesHash.substring(0, 8)  // Log first 8 chars for debugging
          });
          resolve();
        };

        request.onerror = () => {
          log.error('[EntityCache] Failed to write cache:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error writing cache:', error);
    }
  }

  /**
   * Delete cached entities for a user
   */
  async delete(userId: string): Promise<void> {
    try {
      await this.init();
      
      if (!this.db) {
        return;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.delete(userId);

        request.onsuccess = () => {
          log.info('[EntityCache] Cache cleared for user');
          resolve();
        };

        request.onerror = () => {
          log.error('[EntityCache] Failed to delete cache:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error deleting cache:', error);
    }
  }

  /**
   * Clear all cached data (for logout, etc.)
   */
  async clear(): Promise<void> {
    try {
      await this.init();
      
      if (!this.db) {
        return;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.clear();

        request.onsuccess = () => {
          log.info('[EntityCache] All cache cleared');
          resolve();
        };

        request.onerror = () => {
          log.error('[EntityCache] Failed to clear cache:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error clearing cache:', error);
    }
  }

  /**
   * Check if cache exists for user
   */
  async has(userId: string): Promise<boolean> {
    const cacheEntry = await this.get(userId);
    return cacheEntry !== null;
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{ count: number; totalSize: number }> {
    try {
      await this.init();
      
      if (!this.db) {
        return { count: 0, totalSize: 0 };
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.count();

        request.onsuccess = () => {
          resolve({
            count: request.result,
            totalSize: 0 // IndexedDB doesn't provide size info easily
          });
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error getting stats:', error);
      return { count: 0, totalSize: 0 };
    }
  }

  /**
   * Debug: Get all cache entries (for development/troubleshooting)
   * 
   * Usage in browser console:
   * ```
   * const cache = (await import('./services/entityCacheService')).entityCacheService;
   * const entries = await cache.getAllCacheEntries();
   * console.log('Cache entries:', entries);
   * ```
   */
  async getAllCacheEntries(): Promise<CacheEntry[]> {
    try {
      await this.init();
      
      if (!this.db) {
        log.warn('[EntityCache] Database not initialized');
        return [];
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.getAll();

        request.onsuccess = () => {
          const entries = request.result as CacheEntry[];
          log.info('[EntityCache] Retrieved all cache entries:', {
            count: entries.length,
            users: entries.map(e => ({ 
              userId: e.userId.substring(0, 12) + '...', 
              entityCount: e.entities.length,
              age: ((Date.now() - e.timestamp) / (60 * 1000)).toFixed(1) + ' minutes'
            }))
          });
          resolve(entries);
        };

        request.onerror = () => {
          log.error('[EntityCache] Failed to get all entries:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      log.error('[EntityCache] Error getting all entries:', error);
      return [];
    }
  }
}

export const entityCacheService = new EntityCacheService();

